import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import LeadForm from "@/components/LeadForm";
import SEO from "@/components/SEO";
import { RevealLines } from "@/components/Reveal";
import { IMAGES, CAMPUSPATH_URL, TESTGUARD_URL, LOGO, TG_LOGO, OG_IMAGE_CAMPUSPATH, OG_IMAGE_TESTGUARD, resolveProduct } from "@/lib/constants";

const EASE = [0.16, 1, 0.3, 1];

export default function Trial() {
  const [params] = useSearchParams();
  const product = resolveProduct(params.get("product"));
  const isTG = product === "testguard";
  const brand = isTG ? "TestGuard" : "CampusPath";

  const pledge = isTG
    ? "Choose a trial length that fits your exam calendar, anywhere from 45 to 90 days, and put TestGuard to work from the very first proctored session."
    : "Choose a trial length that fits your school calendar, anywhere from 45 to 90 days, and put CampusPath to work from the first bell.";
  const bullets = isTG
    ? [
        "Full platform access, browser lockdown, device monitoring & proctor dashboard",
        "Guided setup with the TestGuard team",
        "Cancel anytime, no card, no commitment",
      ]
    : [
        "Full platform access, dismissal, attendance, gradebook & more",
        "Guided setup with the CampusPath team",
        "Cancel anytime, no card, no commitment",
      ];

  return (
    <main className={`min-h-screen ${isTG ? "dark-scroll bg-obsidian text-white" : "bg-bone"}`} data-testid="trial-page">
      <SEO
        title={`${brand} Free Trial · 45–90 Days | ${brand}`}
        description={`Start a fully-featured ${brand} free trial, 45 to 90 days, no credit card required.`}
        canonical={`${isTG ? TESTGUARD_URL : CAMPUSPATH_URL}/trial`}
        image={isTG ? OG_IMAGE_TESTGUARD : OG_IMAGE_CAMPUSPATH}
        favicon={isTG ? TG_LOGO : LOGO}
      />
      <SiteNav dark={isTG} />
      <section className="mx-auto grid max-w-7xl gap-16 px-6 pt-36 pb-24 md:px-8 md:pt-44 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <span className="text-xs font-600 uppercase tracking-widest text-leaf-600">{brand} Free Trial · 45–90 days</span>
          <h1 className={`mt-5 font-display text-[clamp(2.4rem,5.5vw,4rem)] font-900 leading-[0.96] tracking-tightest ${isTG ? "text-white" : "text-ink"}`}>
            <RevealLines lines={isTG ? ["Run a real", "exam.", "On the house."] : ["Run a real", "dismissal.", "On the house."]} />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.5 }}
            className={`mt-7 max-w-md text-lg leading-relaxed ${isTG ? "text-white/65" : "text-ink/65"}`}
          >
            {pledge}
          </motion.p>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-10 space-y-4"
          >
            {bullets.map((t) => (
              <li key={t} className={`flex items-start gap-3 ${isTG ? "text-white/75" : "text-ink/75"}`}>
                <span className="mt-1 text-leaf-600"><svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg></span>
                {t}
              </li>
            ))}
          </motion.ul>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.85 }}
            className="mt-12 overflow-hidden rounded-2xl"
          >
            <img src={isTG ? IMAGES.examLaptop : IMAGES.students} alt={isTG ? "Students taking a secured exam" : "Students at school"} className="h-56 w-full object-cover" />
          </motion.div>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          <div className={
            isTG
              ? "rounded-[2rem] border border-leaf/25 bg-surface-dark p-8 shadow-[0_0_90px_-20px_rgba(0,200,83,0.5)] ring-1 ring-inset ring-white/5 md:p-10"
              : "rounded-[2rem] border border-ink/10 bg-white p-8 shadow-[0_40px_80px_-40px_rgba(0,34,68,0.35)] md:p-10"
          }>
            <h2 className={`font-display text-2xl font-800 ${isTG ? "text-white" : "text-ink"}`}>Start your trial</h2>
            <p className={`mt-2 text-sm ${isTG ? "text-white/50" : "text-ink/50"}`}>Takes under a minute.</p>
            <div className="mt-8">
              <LeadForm variant="trial" product={product} dark={isTG} />
            </div>
          </div>
        </div>
      </section>
      <SiteFooter dark={isTG} />
    </main>
  );
}
